/* Email → Deliverability: the DNS records (SPF, DKIM, DMARC, return-path) the sending domain needs, plus a checklist. */
import { useEmailDns } from "../../../data/hooks";
import type { EmailConfig } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Alert, Card, Chip, CopyValue, Grid, Loaded } from "../../../ui";
import { providerById } from "./catalog";

const PURPOSE: Record<string, [string, string]> = {
  spf: ["SPF — who may send for the domain", "SPF — من يحق له الإرسال باسم النطاق"],
  dkim: ["DKIM — signs every message", "DKIM — توقيع كل رسالة"],
  dmarc: ["DMARC — policy & reports", "DMARC — السياسة والتقارير"],
  "return-path": ["Return-path — bounces", "مسار الارتداد — الرسائل المرتدة"],
  verification: ["Domain verification", "توثيق النطاق"],
};

export default function DnsTab({ cur }: { cur: EmailConfig }) {
  const { L } = useI18n();
  const q = useEmailDns({ provider: cur.provider.provider, region: cur.provider.region, from: cur.fromAddress });
  const info = providerById(cur.provider.provider);
  const checklist: [string, string][] = [
    ["Verify the sending domain (or the From address) in the provider's console.", "وثّق نطاق الإرسال (أو عنوان المرسل) في لوحة المزوّد."],
    ["Add the SPF, DKIM and DMARC records below at your DNS host, then wait for them to propagate (up to an hour).", "أضف سجلات SPF وDKIM وDMARC أدناه لدى مزوّد DNS ثم انتظر انتشارها (حتى ساعة)."],
    ["Amazon SES: request production access — the sandbox only delivers to verified addresses.", "Amazon SES: اطلب الوصول للإنتاج — وضع التجربة يرسل فقط إلى العناوين الموثقة."],
    ["Send a test to a Gmail and an Outlook inbox and check “Show original”: SPF, DKIM and DMARC should say PASS.", "أرسل تجربة إلى Gmail وOutlook وتحقق من «عرض الأصل»: يجب أن تظهر SPF وDKIM وDMARC بنتيجة PASS."],
    ["Start DMARC at p=none, then move to quarantine once reports are clean.", "ابدأ DMARC بـ p=none ثم انتقل إلى quarantine عند سلامة التقارير."],
  ];
  return (
    <Grid cols="2-1">
      <Card title={L("DNS records", "سجلات DNS")} sub={L("For " + (info?.name ?? cur.provider.provider) + " sending as " + cur.fromAddress, "لـ " + (info?.nameAr ?? cur.provider.provider) + " بالإرسال من " + cur.fromAddress)} testId="email-dns">
        {cur.provider.provider === "log" && <div style={{ marginBottom: 12 }}><Alert tone="info" tint title={L("Log only does not send real email", "وضع السجل فقط لا يرسل بريدًا حقيقيًا")} desc={L("Pick a provider on the Provider tab to see its exact records.", "اختر مزوّدًا في تبويب المزوّد لرؤية سجلاته.")} /></div>}
        <Loaded q={q}>{(g) => (
          <div>
            {g.records.map((r, i) => (
              <div key={i} className="dns-row" data-testid={"dns-" + r.purpose + "-" + i}>
                <span><Chip tone={r.purpose === "dkim" ? "pri" : r.purpose === "dmarc" ? "gold" : undefined} nodot>{r.type}</Chip></span>
                <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                  <b className="sm">{L(...(PURPOSE[r.purpose] ?? [r.purpose, r.purpose]))}</b>
                  <CopyValue label={L("Host", "الاسم")} value={r.host} />
                  <CopyValue label={L("Value", "القيمة")} value={r.value} />
                  {(r.note || r.noteAr) && <span className="xs muted">{L(r.note, r.noteAr || r.note)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}</Loaded>
      </Card>
      <Card title={L("Deliverability checklist", "قائمة التحقق من التسليم")} testId="email-checklist">
        <ol className="sm" style={{ margin: 0, paddingInlineStart: 18, display: "flex", flexDirection: "column", gap: 8 }}>
          {checklist.map(([en, ar], i) => <li key={i}>{L(en, ar)}</li>)}
        </ol>
      </Card>
    </Grid>
  );
}
